// src/screens/mypage/MyInquiriesScreen.js
// 78차 65차: 답변을 읽기만 하던 것을, 관리자 답변 후 문의자도 후속 메시지(재문의)를
// 남길 수 있는 스레드로 확장(MyInquiryCard, inquiryService의 replies 서브컬렉션).
// 72차 신규: 일반 회원이 자기가 남긴 문의(앱 문의 + 매장 문의)와 답변을 모아보는 화면.
// 새 문의는 여기서는 "앱 문의"만 작성 가능 — 매장 문의는 그 매장의 맥락(예약/테마)이
// 이미 있는 화면(예약 내역/테마 상세)에서 시작하는 게 자연스러워 거기에 진입점을 따로 둠.
// 74차: 화면 자체 커스텀 헤더 → 공통 AppHeader로 교체("+ 앱 문의" 버튼은 rightElement로 이동)
// 77차: 74차 교체 시 제목 누락("‹ 뒤로"만 보이던 것 — "AppHeader 없음" 반복 신고 원인) 복원
// 78차: 헤더 "+ 앱 문의" 텍스트 버튼을 공용 TextButton으로 교체
import React, { useState, useEffect, useCallback } from 'react';
import { showAlert } from '../../components/common/AppAlert';
import { View, Text, TextInput, StyleSheet, ScrollView, ActivityIndicator, RefreshControl } from 'react-native';
import { Image } from 'expo-image';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useAuth } from '../../contexts/AuthContext';
import { getMyInquiries, getInquiryReplies, addInquiryReply } from '../../services/inquiryService';
import { markNotificationsReadByType } from '../../services/recruitService';
import { COLORS } from '../../constants/theme';
import { getInquiryCategoryLabel } from '../../constants';
import AppHeader from '../../components/common/AppHeader';
import GradientButton from '../../components/common/GradientButton';
import { TextButton } from '../../components/common/Buttons';
import { MenuInquiryIcon, IconLabel, CheckmarkIcon, HourglassIcon } from '../../components/common/Icons';

const GOLD = COLORS.gold, BG = COLORS.bg, SURFACE = COLORS.surface, SURFACE2 = COLORS.surface2;
const BORDER = COLORS.border, TEXT = COLORS.text, TEXT_MUTED = COLORS.textMuted;
const GREEN = COLORS.green;

const CONTEXT_LABEL = { reservation: '예약 문의', theme: '테마 문의' };

// 78차 65차: 답변을 읽기만 할 수 있던 것을, 문의가 한 번에 해결되지 않을 때 문의자도
// 후속 메시지를 남길 수 있게 스레드(replies 서브컬렉션) 기반으로 교체.
function MyInquiryCard({ item, uid }) {
  const [replies, setReplies] = useState([]);
  const [repliesLoaded, setRepliesLoaded] = useState(false);
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getInquiryReplies(item.id).then(r => { setReplies(r); setRepliesLoaded(true); }).catch(() => setRepliesLoaded(true));
  }, [item.id]);

  // 78차 68차: 78차 65차 스레드 마이그레이션 이전(또는 그 배포 직전 캐시된 클라이언트)의
  // 단일 reply 필드({text, adminUid, repliedAt})만 있고 replies 서브컬렉션은 0건인
  // 문의가 실제로 있었음(라이브 확인) — 그 경우 답변 내용이 화면에서 통째로 사라지고
  // 후속 답장 입력창도 안 뜨던 버그(문의 카드마다 크기가 들쭉날쭉해 보이던 원인).
  const effectiveReplies = replies.length > 0
    ? replies
    : (item.reply?.text ? [{ role: 'admin', text: item.reply.text, createdAt: item.reply.repliedAt, legacy: true }] : []);

  // 78차 67차: item.status(목록 로드 시점 스냅샷) 대신 실제 스레드의 마지막 메시지가
  // 누구 것인지로 직접 계산 — 후속 메시지를 보내면(재오픈) 자동으로 "대기중"이 되고,
  // 화면을 나갔다 오지 않아도, 그리고 관리자가 이 화면이 열려있는 동안 답변해도 항상
  // 정확함(InquiriesScreen.js의 InquiryCard와 동일한 근본 수정).
  const status = repliesLoaded
    ? (effectiveReplies.length === 0 ? item.status : (effectiveReplies[effectiveReplies.length - 1].role === 'admin' ? 'answered' : 'pending'))
    : item.status;

  const handleSend = async () => {
    if (!text.trim()) return;
    setSaving(true);
    try {
      await addInquiryReply(item.id, { uid, role: 'user', text });
      setReplies(prev => [...prev, { role: 'user', uid, text: text.trim(), createdAt: new Date() }]);
      setText('');
    } catch (e) {
      showAlert('오류', '답장 전송 중 오류가 발생했어요.\n' + e.message);
    } finally { setSaving(false); }
  };

  return (
    <View style={s.card}>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 6, flexWrap: 'wrap' }}>
        <View style={s.ctxBadge}>
          <IconLabel Icon={MenuInquiryIcon} size={10} color={TEXT_MUTED} gap={3} textStyle={s.ctxBadgeTxt}>
            {item.targetType === 'app' ? '앱 문의' : (CONTEXT_LABEL[item.context?.type] || '매장 문의')}
          </IconLabel>
        </View>
        {!!item.category && (
          <View style={s.ctxBadge}><Text style={s.ctxBadgeTxt}>{getInquiryCategoryLabel(item.targetType, item.category) || item.category}</Text></View>
        )}
        <View style={[s.statusBadge, status === 'answered' ? s.statusAnswered : s.statusPending]}>
          <IconLabel Icon={status === 'answered' ? CheckmarkIcon : HourglassIcon} size={11}
            color={status === 'answered' ? GREEN : GOLD}
            textStyle={[s.statusTxt, { color: status === 'answered' ? GREEN : GOLD }]}>
            {status === 'answered' ? '답변완료' : '대기중'}
          </IconLabel>
        </View>
      </View>
      <Text style={s.title}>{item.title}</Text>
      <Text style={s.content}>{item.content}</Text>
      {!!item.imageUrl && (
        <Image source={{ uri: item.imageUrl }} style={s.attachedImage} />
      )}

      {!repliesLoaded ? (
        <ActivityIndicator size="small" color={GOLD} style={{ marginTop: 10 }} />
      ) : effectiveReplies.length > 0 ? (
        <View style={{ marginTop: 10, gap: 8 }}>
          {effectiveReplies.map((r, i) => (
            <View key={r.id || i} style={[s.replyBox, r.role === 'user' && s.replyBoxUser]}>
              {r.role === 'admin'
                ? <IconLabel Icon={MenuInquiryIcon} size={12} color={s.replyLabel.color} textStyle={s.replyLabel}>답변</IconLabel>
                : <Text style={s.replyLabel}>↩️ 내 답장</Text>}
              <Text style={s.replyText}>{r.text}</Text>
            </View>
          ))}
        </View>
      ) : null}

      {/* 답변이 있을 때만 후속 메시지(재문의)를 남길 수 있음 — 아직 아무 답변도 없는데
          문의자가 먼저 또 글을 남기는 건 처음 문의를 수정하는 것과 다를 게 없어 혼란만 줌 */}
      {repliesLoaded && effectiveReplies.some(r => r.role === 'admin') && (
        <View style={{ marginTop: 10 }}>
          <TextInput
            style={s.replyInput}
            value={text}
            onChangeText={setText}
            placeholder="답변에 대해 추가로 남길 말이 있으면 적어주세요"
            placeholderTextColor={TEXT_MUTED}
            multiline
          />
          <GradientButton
            label={saving ? '전송 중...' : '답장 보내기'}
            onPress={handleSend}
            disabled={saving}
            style={{ marginTop: 8 }}
          />
        </View>
      )}
    </View>
  );
}

export default function MyInquiriesScreen({ navigation }) {
  const insets = useSafeAreaInsets();
  const { user } = useAuth();
  const [inquiries,  setInquiries]  = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    if (!user?.uid) return;
    try { setInquiries(await getMyInquiries(user.uid)); }
    catch { /* 무음 처리 */ }
    finally { setLoading(false); setRefreshing(false); }
    // 78차 68차: 이 화면에서 답변을 이미 확인했는데도 "문의 내역"/"본사에 문의하기" 배지가
    // 안 없어지던 버그 — 진입 시 관련 알림(inquiry_replied)을 읽음 처리
    markNotificationsReadByType(user.uid, ['inquiry_replied']).catch(() => {});
  }, [user?.uid]);

  useEffect(() => { load(); }, [load]);

  return (
    <View style={s.container}>
      <AppHeader
        showBack
        centerElement={<Text style={{ color: COLORS.text, fontSize: 15, fontWeight: '800' }}>문의 내역</Text>}
        rightElement={
          <TextButton
            label="+ 앱 문의"
            onPress={() => navigation.navigate('WriteInquiry', { targetType: 'app' })}
            textStyle={s.newBtn}
            style={{ alignSelf: 'flex-end' }}
          />
        }
      />

      {loading ? (
        <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
          <ActivityIndicator size="large" color={GOLD} />
        </View>
      ) : (
        <ScrollView
          contentContainerStyle={{ padding: 16, paddingBottom: insets.bottom + 32, gap: 10 }}
          refreshControl={<RefreshControl refreshing={refreshing} onRefresh={() => { setRefreshing(true); load(); }} tintColor={GOLD} />}>
          <Text style={s.hint}>매장 문의는 예약 내역이나 테마 상세 화면에서 작성할 수 있어요.</Text>
          {inquiries.length === 0 ? (
            <Text style={{ color: TEXT_MUTED, textAlign: 'center', marginTop: 40, fontStyle: 'italic' }}>
              아직 등록한 문의가 없어요.
            </Text>
          ) : (
            inquiries.map(item => (
              <MyInquiryCard key={item.id} item={item} uid={user.uid} />
            ))
          )}
        </ScrollView>
      )}
    </View>
  );
}

const s = StyleSheet.create({
  container:   { flex: 1, backgroundColor: BG },
  newBtn:      { color: GOLD, fontSize: 13, fontWeight: '800' },

  hint:        { color: TEXT_MUTED, fontSize: 11, marginBottom: 4 },
  card:        { backgroundColor: SURFACE, borderRadius: 14, borderWidth: 1, borderColor: BORDER, padding: 14 },
  ctxBadge:    { backgroundColor: SURFACE2, borderRadius: 6, borderWidth: 1, borderColor: BORDER, paddingHorizontal: 8, paddingVertical: 2 },
  ctxBadgeTxt: { color: TEXT_MUTED, fontSize: 10, fontWeight: '700' },
  statusBadge: { borderRadius: 6, paddingHorizontal: 8, paddingVertical: 2, borderWidth: 1 },
  statusPending:  { backgroundColor: GOLD + '12', borderColor: GOLD + '40' },
  statusAnswered: { backgroundColor: GREEN + '15', borderColor: GREEN + '40' },
  statusTxt:   { fontSize: 10, fontWeight: '700' },
  title:       { color: TEXT, fontSize: 14, fontWeight: '800', marginBottom: 4 },
  content:     { color: TEXT, fontSize: 13, lineHeight: 19 },
  attachedImage: { width: 160, height: 120, borderRadius: 10, marginTop: 8 },
  replyBox:    { backgroundColor: SURFACE2, borderRadius: 10, borderWidth: 1, borderColor: BORDER, padding: 10 },
  replyBoxUser:{ backgroundColor: GOLD + '0d', borderColor: GOLD + '30' }, // 78차 65차: 내 답장 구분
  replyLabel:  { color: GOLD, fontSize: 12, fontWeight: '800' },
  replyText:   { color: TEXT, fontSize: 13, marginTop: 6, lineHeight: 18 },
  replyInput:  { backgroundColor: SURFACE2, borderWidth: 1, borderColor: BORDER, borderRadius: 8,
                 paddingHorizontal: 12, paddingVertical: 10, color: TEXT, fontSize: 13, minHeight: 60, textAlignVertical: 'top' },
});
